import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getUrlLanguage, isBackOfficeNavigation } from "./requestLanguage";

/**
 * Apply and persist a `?lang=` / `?locale=` query parameter.
 *
 * The policy pages are opened standalone — in the mobile app's WebView, and via
 * `window.open` from the account screen — so the URL is often the only signal
 * for which language to render. `i18n.js` reads it once at bootstrap, which
 * covers a direct load, but two things need this hook:
 *
 *   1. A client-side route change carries a new `?lang=` that bootstrap never
 *      sees, because the module already ran.
 *   2. Bootstrap does not PERSIST the choice. Only the terms page did, via a
 *      copy of this effect, so `/privacy-policy?lang=gu` rendered correctly but
 *      wrote nothing — and the next page opened without a `lang` parameter fell
 *      back to English. Reproduced live: after `privacy-policy?lang=gu`,
 *      `userLanguage` was still null and the accessibility statement rendered
 *      in English; after `terms-and-conditions?lang=gu` it rendered in Gujarati.
 *
 * Shared rather than copied so the three policy pages cannot drift apart again.
 *
 * Back-office links are always English and must not have their saved language
 * overwritten by a stale `lang` left in the URL.
 */
const useUrlLanguage = () => {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (isBackOfficeNavigation(location.pathname, location.state)) return;

    const lang = getUrlLanguage(window.location.search);
    if (!lang) return;

    let cancelled = false;
    const applyUrlLanguage = async () => {
      localStorage.setItem("userLanguage", lang);
      const { loadLanguage } = await import("./i18n");
      if (cancelled) return;
      await loadLanguage(lang);
      if (cancelled) return;
      i18n.changeLanguage(lang);
    };

    applyUrlLanguage();
    return () => {
      cancelled = true;
    };
  }, [i18n, location.pathname, location.search, location.state]);
};

export default useUrlLanguage;
